import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApi, errMsg, money, fmtDate, toCents, downloadBlob } from "@/lib/api";
import { useMe } from "@/lib/me";
import { StatusBadge, Spinner } from "@/components/shared/common";
import { TagInput, FileUpload, FileChips, ImageList } from "@/components/shared/FileUpload";
import { ServiceCard, ProductCard, VendorCard } from "@/components/shared/Cards";
import { useCategories } from "@/pages/Home";
import { DTitle, Metric, DataTable } from "@/pages/dashboard/Layout";

const useGet = (url, deps = []) => {
  const api = useApi();
  const [d, setD] = useState(null);
  const load = () => api.get(url).then((r) => setD(r.data)).catch(() => setD([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [api, url, ...deps]);
  return [d, load];
};
export { useGet };

const orderCols = [
  { h: "Order", r: (o) => <Link to={`/orders/${o.id}`} className="font-mono text-primary" data-testid={`order-link-${o.number}`}>{o.number}</Link> },
  { h: "Item", r: (o) => <span className="line-clamp-1 max-w-xs">{o.title}</span> },
  { h: "Type", r: (o) => <span className="capitalize">{o.kind}</span> },
  { h: "Status", r: (o) => <StatusBadge status={o.status} /> },
  { h: "Total", r: (o) => money(o.total_cents) },
  { h: "Date", r: (o) => fmtDate(o.created_at) },
];
export { orderCols };

export function BuyerOverview() {
  const [d] = useGet("/buyer/overview");
  const { me } = useMe();
  if (!d) return <Spinner />;
  return (
    <div>
      <DTitle title={`Welcome, ${me?.name?.split(" ")[0] || ""}`} text="Your buying activity at a glance."><Button asChild data-testid="overview-post-project"><Link to="/projects/new"><Plus className="mr-1 h-4 w-4" />Post a project</Link></Button></DTitle>
      {me?.invites?.length > 0 && <Invites invites={me.invites} />}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <Metric label="Active orders" value={d.active_orders} testId="metric-active-orders" /><Metric label="Completed" value={d.completed_orders} testId="metric-completed" />
        <Metric label="Purchases" value={d.purchases} testId="metric-purchases" /><Metric label="Open projects" value={d.open_projects} testId="metric-open-projects" />
        <Metric label="Favorites" value={d.favorites} testId="metric-favorites" /><Metric label="Total spent" value={money(d.spent_cents)} testId="metric-spent" />
      </div>
      <h2 className="mb-4 mt-10 text-lg font-semibold">Recent orders</h2>
      <DataTable cols={orderCols} rows={d.recent_orders} empty="No orders yet — explore services to get started." />
    </div>
  );
}

function Invites({ invites }) {
  const api = useApi();
  const { refresh } = useMe();
  return <div className="card-surface mb-6 space-y-2 p-5">{invites.map((v) => (
    <div key={v.id} className="flex items-center justify-between text-sm"><span>You're invited to join <b>{v.display_name}</b></span>
      <Button size="sm" onClick={() => api.post(`/team-invites/${v.id}/accept`).then(() => { toast.success("Joined team"); refresh(); }).catch((e) => toast.error(errMsg(e)))} data-testid={`accept-invite-${v.id}`}>Accept</Button></div>))}</div>;
}

export function BuyerOrders() {
  const [kind, setKind] = useState("");
  const [d] = useGet(`/orders?role=buyer${kind ? `&kind=${kind}` : ""}`, [kind]);
  return (
    <div>
      <DTitle title="Orders & contracts" text="Service orders, project contracts and product purchases." />
      <Tabs value={kind || "all"} onValueChange={(v) => setKind(v === "all" ? "" : v)} className="mb-4"><TabsList>
        <TabsTrigger value="all" data-testid="orders-tab-all">All</TabsTrigger><TabsTrigger value="service" data-testid="orders-tab-service">Services</TabsTrigger>
        <TabsTrigger value="contract" data-testid="orders-tab-contract">Contracts</TabsTrigger><TabsTrigger value="product" data-testid="orders-tab-product">Products</TabsTrigger></TabsList></Tabs>
      <DataTable cols={orderCols} rows={d?.items} empty="No orders yet." testId="buyer-orders-table" />
    </div>
  );
}

export function Purchases() {
  const api = useApi();
  const [d] = useGet("/orders?role=buyer&kind=product&status=completed");
  return (
    <div>
      <DTitle title="Purchases" text="Digital products you own. Downloads are authorized against your purchase." />
      <DataTable rows={d?.items} empty="No purchases yet." testId="purchases-table" cols={[
        { h: "Product", r: (o) => <Link to={`/products/${o.product_slug}`} className="font-medium hover:text-primary">{o.title}</Link> },
        { h: "License", r: (o) => <span className="capitalize">{o.license}</span> }, { h: "Paid", r: (o) => money(o.total_cents) }, { h: "Date", r: (o) => fmtDate(o.paid_at) },
        { h: "", r: (o) => <Button size="sm" variant="outline" data-testid={`download-${o.id}`} onClick={() => downloadBlob(api, `/products/${o.product_id}/download`, `${o.product_slug}.zip`).catch(() => toast.error("No file available for this product yet"))}><Download className="mr-1 h-4 w-4" />Download</Button> },
      ]} />
    </div>
  );
}

export function MyProjects() {
  const api = useApi();
  const [d, load] = useGet("/projects/mine");
  return (
    <div>
      <DTitle title="My projects" text="Projects you've posted and their proposals."><Button asChild data-testid="post-project-button"><Link to="/projects/new"><Plus className="mr-1 h-4 w-4" />Post project</Link></Button></DTitle>
      <DataTable rows={d} empty="You haven't posted any projects." testId="my-projects-table" cols={[
        { h: "Project", r: (p) => <Link to={`/projects/${p.id}`} className="font-medium hover:text-primary">{p.title}</Link> }, { h: "Status", r: (p) => <StatusBadge status={p.status} /> },
        { h: "Proposals", r: (p) => p.proposals_count }, { h: "Budget", r: (p) => `${money(p.budget_min_cents)}–${money(p.budget_max_cents)}` }, { h: "Posted", r: (p) => fmtDate(p.created_at) },
        { h: "", r: (p) => p.status === "open" && <Button size="sm" variant="ghost" onClick={() => api.patch(`/projects/${p.id}/close`).then(load)} data-testid={`close-project-${p.id}`}>Close</Button> },
      ]} />
    </div>
  );
}

export function Favorites() {
  const [d] = useGet("/favorites");
  return (
    <div><DTitle title="Favorites" />
      {!d ? <Spinner /> : d.length === 0 ? <DataTable rows={[]} cols={[]} empty="Save services, products and sellers to find them here." /> :
        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">{d.map((f) => f.kind === "service" ? <ServiceCard key={f.item.id} s={f.item} /> : f.kind === "product" ? <ProductCard key={f.item.id} p={f.item} /> : <VendorCard key={f.item.id} v={f.item} />)}</div>}
    </div>
  );
}

export function Payments() {
  const [d] = useGet("/buyer/payments");
  return (
    <div><DTitle title="Payments & invoices" text="Every Stripe checkout you started. Click an order for its receipt." />
      <DataTable rows={d} empty="No payments yet." testId="payments-table" cols={[
        { h: "Order", r: (t) => <Link to={`/orders/${t.order_id}`} className="font-mono text-primary">{t.order_number}</Link> }, { h: "Description", r: (t) => <span className="line-clamp-1">{t.milestone_title}</span> },
        { h: "Amount", r: (t) => money(t.amount_cents) }, { h: "Status", r: (t) => <StatusBadge status={t.payment_status === "paid" ? "completed" : t.payment_status} /> }, { h: "Date", r: (t) => fmtDate(t.created_at) },
        { h: "", r: (t) => t.payment_status === "paid" && <Link to={`/invoice/${t.order_id}`} className="text-sm text-primary" data-testid={`invoice-${t.session_id}`}>Invoice</Link> },
      ]} />
    </div>
  );
}

export function Invoice() {
  const api = useApi();
  const [d, setD] = useState(null);
  const id = window.location.pathname.split("/").pop();
  useEffect(() => { api.get(`/orders/${id}`).then((r) => setD(r.data.order)); }, [api, id]);
  if (!d) return <div className="container-x py-20"><Spinner /></div>;
  return (
    <div className="container-x max-w-2xl py-12">
      <div className="card-surface p-10" data-testid="invoice">
        <div className="flex justify-between"><div><p className="text-xl font-extrabold">DevMarket</p><p className="text-sm text-muted-foreground">Receipt</p></div><div className="text-right font-mono text-sm">{d.number}<br />{fmtDate(d.paid_at || d.created_at)}</div></div>
        <div className="mt-8 grid grid-cols-2 gap-4 text-sm"><div><p className="text-muted-foreground">Billed to</p><p>{d.buyer_name}</p></div><div><p className="text-muted-foreground">Seller</p><p>{d.vendor_name}</p></div></div>
        <table className="mt-8 w-full text-sm"><tbody>
          <tr className="border-b border-border"><td className="py-2">{d.title}</td><td className="py-2 text-right">{money(d.subtotal_cents)}</td></tr>
          {d.discount_cents > 0 && <tr><td className="py-2">Discount ({d.coupon_code})</td><td className="py-2 text-right">−{money(d.discount_cents)}</td></tr>}
          <tr className="font-bold"><td className="py-2">Total</td><td className="py-2 text-right">{money(d.total_cents)}</td></tr></tbody></table>
        <p className="mt-6 text-xs text-muted-foreground">Status: {d.paid ? "Paid via Stripe" : "Unpaid"}. Prices in INR (₹).</p>
        <Button className="mt-6 print:hidden" variant="outline" onClick={() => window.print()} data-testid="print-invoice">Print</Button>
      </div>
    </div>
  );
}

export function Refunds() {
  const [d] = useGet("/buyer/refunds");
  const cols = [{ h: "Order", r: (r) => <Link to={`/orders/${r.order_id}`} className="font-mono text-primary">{r.order_number}</Link> }, { h: "Item", r: (r) => r.title },
    { h: "Reason", r: (r) => <span className="line-clamp-2 max-w-sm">{r.reason}</span> }, { h: "Status", r: (r) => <StatusBadge status={r.status} /> }, { h: "Date", r: (r) => fmtDate(r.created_at) }];
  return (
    <div><DTitle title="Refunds & disputes" text="Open these from an order page." />
      <h2 className="mb-3 font-semibold">Refund requests</h2><DataTable rows={d?.refunds} cols={cols} empty="No refund requests." testId="refunds-table" />
      <h2 className="mb-3 mt-8 font-semibold">Disputes</h2><DataTable rows={d?.disputes} cols={cols} empty="No disputes." testId="disputes-table" />
    </div>
  );
}

export function Support() {
  const api = useApi();
  const [d, load] = useGet("/tickets/mine");
  const [f, setF] = useState({ subject: "", message: "" });
  const [reply, setReply] = useState({});
  const submit = async (e) => { e.preventDefault(); try { await api.post("/tickets", f); setF({ subject: "", message: "" }); toast.success("Ticket created"); load(); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <div><DTitle title="Support" text="Our team replies inside the ticket." />
      <form onSubmit={submit} className="card-surface mb-8 space-y-3 p-6" data-testid="ticket-form">
        <Input required placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="ticket-subject-input" />
        <Textarea required minLength={10} placeholder="Describe the issue" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="ticket-message-input" />
        <Button type="submit" data-testid="ticket-submit-button">Open ticket</Button>
      </form>
      {!d ? <Spinner /> : d.map((t) => (
        <div key={t.id} className="card-surface mb-4 space-y-2 p-5" data-testid={`ticket-${t.id}`}>
          <div className="flex justify-between"><p className="font-semibold">{t.subject}</p><StatusBadge status={t.status} /></div>
          <p className="text-sm text-muted-foreground">{t.message}</p>
          {t.replies.map((r, i) => <p key={i} className={`rounded-lg p-2 text-sm ${r.by === "support" ? "bg-accent" : "bg-secondary"}`}><b>{r.by === "support" ? "Support" : "You"}:</b> {r.message}</p>)}
          {t.status !== "closed" && <div className="flex gap-2"><Input placeholder="Reply" value={reply[t.id] || ""} onChange={(e) => setReply({ ...reply, [t.id]: e.target.value })} />
            <Button size="sm" onClick={() => api.post(`/tickets/${t.id}/reply`, { message: reply[t.id] }).then(() => { setReply({ ...reply, [t.id]: "" }); load(); }).catch((e) => toast.error(errMsg(e)))}>Send</Button></div>}
        </div>))}
    </div>
  );
}

export function ProfileSettings() {
  const api = useApi();
  const { me, refresh } = useMe();
  const [f, setF] = useState(null);
  useEffect(() => { if (me && !f) setF({ name: me.name, bio: me.bio || "", skills: me.skills || [], languages: me.languages || [], timezone: me.timezone || "", avatar_url: me.avatar_url || "", portfolio_links: me.portfolio_links || [] }); }, [me, f]);
  if (!f) return <Spinner />;
  const save = async (e) => { e.preventDefault(); try { await api.put("/me", f); toast.success("Profile saved"); refresh(); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <form onSubmit={save} className="max-w-2xl space-y-5" data-testid="profile-form">
      <DTitle title="Profile settings" text={`Signed in as ${me.email}. Password, email and sessions are managed from the account menu (top right).`} />
      <div><Label>Display name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="profile-name-input" /></div>
      <div><Label>Bio</Label><Textarea rows={4} value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} data-testid="profile-bio-input" /></div>
      <div><Label>Skills</Label><TagInput value={f.skills} onChange={(v) => setF({ ...f, skills: v })} testId="profile-skills-input" /></div>
      <div><Label>Languages</Label><TagInput value={f.languages} onChange={(v) => setF({ ...f, languages: v })} testId="profile-languages-input" /></div>
      <div><Label>Portfolio links</Label><TagInput value={f.portfolio_links} onChange={(v) => setF({ ...f, portfolio_links: v })} placeholder="https://…" testId="profile-links-input" /></div>
      <div><Label>Timezone</Label><Input value={f.timezone} onChange={(e) => setF({ ...f, timezone: e.target.value })} placeholder="e.g. Europe/Berlin" data-testid="profile-timezone-input" /></div>
      <div><Label>Avatar</Label><ImageList images={f.avatar_url ? [f.avatar_url] : []} setImages={(imgs) => setF({ ...f, avatar_url: imgs.slice(-1)[0] || "" })} testId="profile-avatar" /></div>
      <Button type="submit" data-testid="profile-save-button">Save profile</Button>
    </form>
  );
}

export function PostProject() {
  const api = useApi();
  const nav = useNavigate();
  const cats = useCategories();
  const [f, setF] = useState({ title: "", description: "", category_slug: "", skills: [], budget_type: "fixed", budget_min: "", budget_max: "", duration: "1-4 weeks", deadline: "", experience_level: "intermediate" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/projects", { ...f, budget_min_cents: toCents(f.budget_min), budget_max_cents: toCents(f.budget_max), attachment_ids: files.map((x) => x.id) });
      toast.success("Project posted"); nav(`/projects/${data.id}`);
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="container-x max-w-3xl py-12">
      <DTitle title="Post a project" text="Describe the work. Developers will send proposals with their price and milestones." />
      <form onSubmit={submit} className="card-surface space-y-5 p-8" data-testid="post-project-form">
        <div><Label>Title</Label><Input required minLength={10} value={f.title} onChange={set("title")} placeholder="e.g. Build a Stripe-powered booking app" data-testid="project-title-input" /></div>
        <div><Label>Description</Label><Textarea required minLength={40} rows={7} value={f.description} onChange={set("description")} placeholder="Goals, scope, tech constraints, deliverables (min 40 chars)" data-testid="project-description-input" /></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label>Category</Label><Select value={f.category_slug} onValueChange={(v) => setF({ ...f, category_slug: v })}><SelectTrigger data-testid="project-category-select"><SelectValue placeholder="Choose" /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Experience level</Label><Select value={f.experience_level} onValueChange={(v) => setF({ ...f, experience_level: v })}><SelectTrigger data-testid="project-level-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="entry">Entry</SelectItem><SelectItem value="intermediate">Intermediate</SelectItem><SelectItem value="expert">Expert</SelectItem></SelectContent></Select></div>
        </div>
        <div><Label>Skills</Label><TagInput value={f.skills} onChange={(v) => setF({ ...f, skills: v })} testId="project-skills-input" /></div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div><Label>Budget type</Label><Select value={f.budget_type} onValueChange={(v) => setF({ ...f, budget_type: v })}><SelectTrigger data-testid="project-budget-type-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="fixed">Fixed</SelectItem><SelectItem value="hourly">Hourly</SelectItem></SelectContent></Select></div>
          <div><Label>Min (₹)</Label><Input type="number" min="10" required value={f.budget_min} onChange={set("budget_min")} data-testid="project-budget-min-input" /></div>
          <div><Label>Max (₹)</Label><Input type="number" min="10" required value={f.budget_max} onChange={set("budget_max")} data-testid="project-budget-max-input" /></div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label>Duration</Label><Input value={f.duration} onChange={set("duration")} data-testid="project-duration-input" /></div>
          <div><Label>Deadline</Label><Input type="date" value={f.deadline} onChange={set("deadline")} data-testid="project-deadline-input" /></div>
        </div>
        <div className="space-y-2"><Label>Attachments (private to you)</Label><FileChips files={files} onRemove={(id) => setFiles(files.filter((x) => x.id !== id))} /><FileUpload onUploaded={(x) => setFiles((p) => [...p, x])} testId="project-attachment-input" /></div>
        <Button type="submit" disabled={busy || !f.category_slug} size="lg" data-testid="project-submit-button">{busy ? "Posting…" : "Post project"}</Button>
      </form>
    </div>
  );
}
